import * as Yup from "yup";

import {
  BusinessGroupPicker,
  BusinessPicker,
  InsurerPicker,
  OfficeButton,
  ReInsurerPicker,
  SelectInput,
  Show,
  SwitchInput,
  TableComponent,
  TextInput,
} from "components";
import {
  ReportOnQuery,
  ReportOnQueryVariables,
} from "_graphql/queries/__generated__/ReportOnQuery";
import { classNames, wrapClick } from "utils";

import { FILTERBYDEDUCTION } from "_graphql/__generated__/globalTypes";
import { Switch } from "@headlessui/react";
import { generateReport } from "_graphql/visal/queries/reports";
import { useBatchReportContext } from "providers/batch-report-provider";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";

const initialValues = {
  business_category: [],
  business_id: [],
  client_type: "",
  offer_source: "",
  insurer_id: [],
  reinsurer_id: [],
  from: "",
  to: "",
  period_status: [],
  offer_status: [],
  payment_status: [],
  showAdvancedFilters: false,
  deduction: "",
  convert_to: "",
  convert_from: [],
  queryOnPayment: false,
  endorsement: false,
};

const validationSchema = Yup.object().shape({
  showAdvancedFilters: Yup.boolean().notRequired(),
  business_category: Yup.array()
    .min(1, "Select business group")
    .required("Business category is required"),
  business_id: Yup.array()
    .min(1, "Select one business")
    .required("Class of business is required"),
  client_type: Yup.string().required("Client type is required"),
  // inurer_id or reinsurer_id is required when client type is selected and value is either insurer or reinsurer
  insurer_id: Yup.array().when("client_type", {
    is: (val: string) => val === "Insurer",
    then: (schema) =>
      schema
        .min(1, "At least one Insurer is Required")
        .required("Insurer is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  reinsurer_id: Yup.array().when("client_type", {
    is: (val: string) => val === "Reinsurer",
    then: (schema) =>
      schema
        .min(1, "At least one Reinsurer is Required")
        .required("Reinsurer is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  deduction: Yup.string().when(["client_type", "showAdvancedFilters"], {
    is: (val: string, val2: boolean) => val === "Reinsurer" && val2 === true,
    then: (schema) => schema.required("Deduction is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  from: Yup.string().required("From date is required"),
  to: Yup.string().required("To date is required"),
  period_status: Yup.array()
    .min(1, "Select offer period")
    .required("Offer period is required"),
  offer_status: Yup.array()
    .min(1, "Select offer status")
    .required("Offer status is required"),
});

const ReportingTab = () => {
  // const { showBanner } = useBatchReportContext();
  const [generate, { loading, data }] = useMutation<
    ReportOnQuery,
    ReportOnQueryVariables
  >(generateReport, {
    onCompleted: (data) => {
      // if (JSON.parse(data.reportOnQuery || "{}")?.can_queue_report) {
      //   showBanner();
      // }
    },
  });
  const form = useFormik<any>({
    initialValues,
    validationSchema,
    onSubmit: () => {
      generate({
        variables: {
          data: {
            ...form.values,
            deduction:
              form.values.client_type === "Reinsurer"
                ? form.values.deduction
                : undefined,
            business_category: undefined,
            showAdvancedFilters: undefined,
            insurer_id:
              form.values.client_type === "Insurer"
                ? form.values.insurer_id
                : undefined,
            reinsurer_id:
              form.values.client_type === "Reinsurer"
                ? form.values.reinsurer_id
                : undefined,
            payment_from: form.values.from,
            payment_to: form.values.to,
          },
        },
      })
        .then(({ data }) => {})
        .catch((err) => {});
    },
  });
  return (
    <div className="w-full">
      <div className="py-3 flex justify-between">
        <div>
          <h1 className="font-bold text-primary text-lg">Reporting</h1>
        </div>
        <div className="flex items-center space-x-2">
          {/* <OfficeButton onClick={() => {
            showBanner();
          }}>
            Show Banner
          </OfficeButton> */}
          <OfficeButton
            disabled={loading}
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Generating ..." : "Generate Report"}
          </OfficeButton>
          {JSON.parse(data?.reportOnQuery || "{}")?.downloadable_xlsx_link && (
            <a
              target="_blank"
              className="bg-teal-500 text-white p-2 rounded-md shadow-sm"
              href={`${
                JSON.parse(data?.reportOnQuery || "{}")?.downloadable_xlsx_link
              }`}
            >
              Download Excel Report
            </a>
          )}
        </div>
      </div>
      <form className="space-y-6 divide-y shadow-sm rounded-md bg-white p-6 divide-gray-200">
        <div>
          <span className="text-xs font-light">Reporting Parameters</span>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <BusinessGroupPicker
                isMulti
                id={"business_category"}
                placeholder="select business category"
                label={"Business Group"}
                {...form}
              />
            </div>
            <div>
              <BusinessPicker
                addAllOption
                filter={form.values.business_category}
                isMulti
                rawId
                id={"business_id"}
                placeholder="select class of business"
                label={"Class of Business"}
                {...form}
              />
            </div>
            <div>
              <SelectInput
                id={"client_type"}
                label={"Client Type"}
                options={[
                  { label: "Reinsurer", value: "Reinsurer" },
                  { label: "Insurer", value: "Insurer" },
                ]}
                {...form}
              />
            </div>

            {form.values.client_type === "Insurer" ? (
              <div className="">
                <InsurerPicker
                  addAllOption
                  isMulti
                  id={"insurer_id"}
                  rawId
                  label={"Insurer"}
                  placeholder="select Insurer"
                  {...form}
                />
              </div>
            ) : (
              <div className="">
                <ReInsurerPicker
                  addAllOption
                  isMulti
                  rawId
                  id={"reinsurer_id"}
                  placeholder="select reinsurer"
                  label={"Reinsurer"}
                  {...form}
                />
                <div className="flex justify-end pt-1">
                  <span
                    onClick={() =>
                      form.setFieldValue(
                        "showAdvancedFilters",
                        !form.values.showAdvancedFilters
                      )
                    }
                    className=" text-xs text-primary-500 text-right cursor-pointer"
                  >
                    {form.values.showAdvancedFilters
                      ? "Hide Advanced Filters"
                      : "Show Advanced Filters"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        {form.values.showAdvancedFilters && (
          <div className="pt-6">
            <span className="text-xs font-light">Advanced Filter</span>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <SelectInput
                  id="deduction"
                  label="Filter by"
                  options={[
                    { label: "NIC Levy", value: FILTERBYDEDUCTION.NIC },
                    { label: "Withholding Tax", value: FILTERBYDEDUCTION.WHT },
                  ]}
                  required={true}
                  {...form}
                />
              </div>
              <div>
                <SelectInput
                  id={"offer_source"}
                  label={"Offer Source"}
                  options={[
                    { label: "Place It", value: "PLACEIT" },
                    { label: "Internal", value: "INTERNAL" },
                  ]}
                  {...form}
                />
              </div>
            </div>
          </div>
        )}
        <div className="pt-6">
          <span className="text-xs font-light">Offer Activity</span>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <SelectInput
                id="period_status"
                label="Filter by offer period"
                multiple
                options={[
                  { label: "Active", value: "ACTIVE" },
                  { label: "Expired", value: "EXPIRED" },
                  { label: "Expiring", value: "EXPIRING" },
                ]}
                required={true}
                {...form}
              />
            </div>
            <div>
              <SelectInput
                id="offer_status"
                label="Filter by offer status"
                multiple
                options={getOfferStatuses(form.values.offer_status)}
                required={true}
                {...form}
              />
            </div>
            {(form.values.offer_status.includes("CLOSED") ||
              form.values.offer_status.includes("All")) && (
              <div>
                <SelectInput
                  id="payment_status"
                  label="Filter by Payment status"
                  multiple
                  options={[
                    { label: "PAID", value: "PAID" },
                    { label: "PART PAYMENT", value: "PART PAYMENT" },
                    // { label: "PART PAID", value: "PAID PAID" },
                    { label: "UNPAID", value: "UNPAID" },
                  ]}
                  required={true}
                  {...form}
                />
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 pt-6">
          <div className="">
            <SwitchInput
              {...form}
              id="queryOnPayment"
              description="Query based on premium payment date"
              label="Query on payment"
            />
          </div>
          <div className="">
            <SwitchInput
              {...form}
              id="endorsement"
              description="Generate report based on endorsement data"
              label="Endorsement Report"
            />
          </div>
          <div>
            <SwitchInput
              {...form}
              id="includeCancelledOffers"
              label={"Include Cancelled Offers"}
              description="Toggle this to include cancelled offers in the reports"
            />
          </div>
        </div>

        <div className="pt-6">
          <span className="text-xs font-light">Query Date Range</span>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <TextInput
                id="from"
                label={form.values.queryOnPayment ? "Payment from" : "From"}
                labelColor="text-primary-500"
                type="date"
                placeholder=""
                required={true}
                {...form}
              />
            </div>
            <div>
              <TextInput
                id="to"
                label={form.values.queryOnPayment ? "Payment to" : "To"}
                labelColor="text-primary-500"
                type="date"
                placeholder=""
                required={true}
                {...form}
              />
            </div>
          </div>
        </div>
      </form>

      <Show
        if={
          Boolean(data) &&
          Boolean(JSON.parse(data?.reportOnQuery || "{}")?.can_queue_report)
        }
      >
        <div className="flex flex-col items-center justify-center py-8 bg-teal-400 rounded-md shadow-md mt-11">
          <svg
            className="w-16 h-16 text-primary-500 mb-4"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 12l2.5 2.5L16 9"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
          <h2 className="text-lg font-semibold text-primary-700 mb-2">
            Large datasets are optimized for background jobs
          </h2>
          <p className="text-sm text-gray-600 text-center max-w-xs">
            Your report involves a very large dataset, so we've queued it to
            process in the background. You'll find the completed report in the{" "}
            <span className="font-medium text-primary-600">
              Batched Reports{" "}
            </span>{" "}
            tab once it's ready.
          </p>
        </div>
      </Show>

      <Show
        if={
          Boolean(data) &&
          !Boolean(JSON.parse(data?.reportOnQuery || "{}")?.can_queue_report)
        }
      >
        <div className="flex flex-1 overflow-y-auto">
          <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
            <TableComponent
              loading={loading}
              title={"reports"}
              columns={[
                {
                  name: "Policy #",
                  accessor: "policy_number",
                },
                {
                  name: "Offer Source",
                  accessor: "offer_from",
                },
                {
                  name: "Insurer",
                  accessor: "insurer_company_name",
                },
                {
                  name: "Insured",
                  accessor: "insured_by",
                },
                {
                  name: "Business Name",
                  accessor: "business_name",
                },
                {
                  name: "Currency",
                  accessor: "currency",
                },
                {
                  name: "Fac. Sum Insured",
                  accessor: "fac_sum_insured",
                },
                {
                  name: "Fac. Premium",
                  accessor: "fac_premium",
                },
                {
                  name: "Brokerage",
                  accessor: "brokerage_amount",
                },
                {
                  name: "Amount Due",
                  accessor: "amount_due",
                },
                {
                  name: "Period of Insurance",
                  accessor: "period",
                },
                {
                  name: "Offer Status",
                  accessor: "offer_status",
                },
                {
                  name: "Payment Status",
                  accessor: "payment_status",
                },
                {
                  name: "Offer Date",
                  accessor: "offer_date",
                },
              ]}
              data={JSON.parse(data?.reportOnQuery || "{}")?.query_output || []}
              refetch={form.handleSubmit}
            />
          </div>
        </div>
      </Show>
    </div>
  );
};

export default ReportingTab;

const getOfferStatuses = (selected: string[]) => {
  // if the selected array contains all, don't show the rest
  if (selected.includes("ALL")) {
    return [{ label: "ALL", value: "ALL" }];
  }
  // if the selected array contains open, don't show the all as well
  if (
    selected.includes("OPEN") ||
    selected.includes("PENDING") ||
    selected.includes("CLOSED")
  ) {
    return [
      { label: "OPEN", value: "OPEN" },
      { label: "PENDING", value: "PENDING" },
      { label: "CLOSED", value: "CLOSED" },
    ];
  }
  return [
    { label: "ALL", value: "ALL" },
    { label: "OPEN", value: "OPEN" },
    { label: "PENDING", value: "PENDING" },
    { label: "CLOSED", value: "CLOSED" },
  ];
};
